import React, { useCallback, useEffect, useState } from 'react';
import { useRateCards, loadRateCards } from '@/lib/rateCards';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { X, Loader2, KeyRound, Plus, Trash2 } from 'lucide-react';
import { tagThemeClass } from './tagTheme';

const INPUT = 'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]';
const BASE = '/notes/tag-workspace/admin/view-passwords';
const EMPTY = { label: '', password: '', rm_mode: 'collapsed', show_pieces: false, show_image: false, expires_on: '', price_mode: 'none', rate_card_id: '' };

function ViewSelect({ rm, pcs, img, pm = 'none', card = '', onChange, testid }) {
  const cards = useRateCards();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={rm} onChange={(e) => onChange({ rm_mode: e.target.value })} className={`${INPUT} w-auto`} data-testid={`${testid}-rm`}>
        <option value="collapsed">Collapsed</option>
        <option value="expanded">Expanded (with notes)</option>
        <option value="raw">Raw (no tag rules)</option>
      </select>
      <label className="flex items-center gap-1.5 text-sm text-foreground">
        <input type="checkbox" checked={pcs} onChange={(e) => onChange({ show_pieces: e.target.checked })} data-testid={`${testid}-pieces`} /> With pieces
      </label>
      <label className="flex items-center gap-1.5 text-sm text-foreground">
        <input type="checkbox" checked={img} onChange={(e) => onChange({ show_image: e.target.checked })} data-testid={`${testid}-image`} /> Image
      </label>
      <select value={pm} onChange={(e) => onChange({ price_mode: e.target.value, rate_card_id: e.target.value === 'none' ? '' : (card || cards[0]?.id || '') })} className={`${INPUT} w-auto`} title="Prices shown on the QR tag" data-testid={`${testid}-price-mode`}>
        <option value="none">Without prices</option>
        <option value="total">With prices: total sale value</option>
        <option value="lines">With prices: amount per raw material</option>
      </select>
      {pm !== 'none' ? (
        <select value={card} onChange={(e) => onChange({ price_mode: pm, rate_card_id: e.target.value })} className={`${INPUT} w-auto`} title="Rate card used for the sale value" data-testid={`${testid}-rate-card`}>
          {!card ? <option value="">Choose rate card…</option> : null}
          {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      ) : null}
    </div>
  );
}

function PasswordRow({ p, onChange }) {
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState('');
  const patch = async (body, msg) => {
    try { await notesApi.patch(`${BASE}/${p.id}`, body); toast.success(msg); onChange(); }
    catch (e) { toast.error(extractError(e, 'Could not update.')); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${p.label}"? Visitors using this password will no longer see tags.`)) return;
    try { await notesApi.delete(`${BASE}/${p.id}`); toast.success('Password deleted.'); onChange(); }
    catch (e) { toast.error(extractError(e, 'Could not delete.')); }
  };
  return (
    <tr className="border-t border-border align-top" data-testid="tagws-view-password-row">
      <td className="px-3 py-2"><div className="font-medium text-foreground">{p.label}</div><div className="font-mono text-xs text-muted-foreground">{p.hint}</div></td>
      <td className="px-3 py-2"><ViewSelect rm={p.rm_mode} pcs={!!p.show_pieces} img={!!p.show_image} pm={p.price_mode} card={p.rate_card_id || ''} onChange={(b) => patch(b, 'View updated.')} testid="tagws-view-password-row" /></td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <input type="date" value={p.expires_on || ''} onChange={(e) => patch({ expires_on: e.target.value }, e.target.value ? `Expires on ${e.target.value}.` : 'Expiry removed.')}
            className={`${INPUT} h-8 w-[150px] ${p.expired ? 'border-destructive text-destructive' : ''}`} data-testid="tagws-view-password-expiry-input" />
          {p.expires_on ? <button onClick={() => patch({ expires_on: '' }, 'Expiry removed.')} className="text-xs text-muted-foreground hover:text-foreground" data-testid="tagws-view-password-expiry-clear">Clear</button> : null}
        </div>
        <div className={`mt-1 text-[length:calc(var(--fs,1)*0.6875rem)] ${p.expired ? 'font-semibold text-destructive' : 'text-muted-foreground'}`} data-testid="tagws-view-password-expiry-status">
          {p.expired ? 'Expired — switched off' : p.expires_on ? `Works until end of ${p.expires_on}` : 'No expiry'}
        </div>
      </td>
      <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!p.active} onChange={(e) => patch({ active: e.target.checked }, e.target.checked ? 'Enabled.' : 'Disabled.')} data-testid="tagws-view-password-active-toggle" /></td>
      <td className="px-3 py-2 text-right">
        {pwOpen ? (
          <div className="flex items-center justify-end gap-1.5">
            <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className={`${INPUT} h-8 w-36`} data-testid="tagws-view-password-new-input" />
            <button onClick={() => { if (pw.trim().length < 4) { toast.error('At least 4 characters.'); return; } patch({ password: pw.trim() }, 'Password changed.'); setPwOpen(false); setPw(''); }} className="rounded-lg bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground" data-testid="tagws-view-password-new-save">Save</button>
            <button onClick={() => { setPwOpen(false); setPw(''); }} className="rounded-lg border border-border px-2.5 py-1.5 text-xs">Cancel</button>
          </div>
        ) : (
          <div className="flex justify-end gap-1.5">
            <button onClick={() => setPwOpen(true)} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs hover:bg-secondary" data-testid="tagws-view-password-change-button"><KeyRound size={12} /> Change</button>
            <button onClick={remove} aria-label="Delete" className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:text-destructive" data-testid="tagws-view-password-delete-button"><Trash2 size={14} /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

// Super admin console: which password shows the QR-scan tag in which view.
export function TagViewPasswordsAdmin({ open, onClose }) {
  const [list, setList] = useState([]);
  useEffect(() => { loadRateCards(); }, []);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState(EMPTY);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await notesApi.get(BASE); setList(data || []); }
    catch (e) { toast.error(extractError(e, 'Could not load passwords.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const create = async (e) => {
    e.preventDefault();
    try { await notesApi.post(BASE, { ...f, password: f.password.trim(), expires_on: f.expires_on || null }); toast.success('Password added.'); setF(EMPTY); load(); }
    catch (err) { toast.error(extractError(err, 'Could not add the password.')); }
  };
  if (!open) return null;
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-view-passwords-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-border bg-card p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">QR tag passwords</h3>
            <p className="text-xs text-muted-foreground">Each password opens the scanned tag in its own view. No expiry date means the password never expires. Passwords are stored securely and can only be changed, not read back.</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="tagws-view-passwords-close-button" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <form onSubmit={create} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/40 p-3 [&>input]:min-w-[200px] [&>input]:flex-1" data-testid="tagws-view-password-create-form">
          <input required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Label (e.g. Customer – collapsed)" className={INPUT} data-testid="tagws-view-password-label-input" />
          <input required minLength={4} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password (e.g. tag999)" className={INPUT} data-testid="tagws-view-password-input" />
          <ViewSelect rm={f.rm_mode} pcs={f.show_pieces} img={f.show_image} pm={f.price_mode} card={f.rate_card_id} onChange={(b) => setF({ ...f, ...b })} testid="tagws-view-password-new" />
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">Expires
            <input type="date" value={f.expires_on} onChange={(e) => setF({ ...f, expires_on: e.target.value })} className={`${INPUT} h-9 w-[150px]`} data-testid="tagws-view-password-new-expiry" />
          </label>
          <button type="submit" className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90" data-testid="tagws-view-password-add-button"><Plus size={15} /> Add</button>
        </form>
        <div className="mt-4 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="tagws-view-passwords-table">
            <thead className="bg-secondary/60 text-left text-[length:calc(var(--fs,1)*0.6875rem)] uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-3 py-2">Label / password</th><th className="px-3 py-2">Tag view</th><th className="px-3 py-2">Expiry</th><th className="px-3 py-2 text-center">Active</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center"><Loader2 size={16} className="mx-auto animate-spin text-muted-foreground" /></td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="tagws-view-passwords-empty">No passwords yet — the original universal password still works until you add one.</td></tr>
              ) : list.map((p) => <PasswordRow key={p.id} p={p} onChange={load} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
